import {
  Checkbox,
  ControlledDropDown,
  CustomRadioGroup,
  DATE_FIELD_TYPES,
  DropdownType,
  FormDateInput,
  FormInput,
  MultiLineInput,
  openNewWindow,
  PhoneInput,
} from '@ui-kit'
import { isEmpty } from 'lodash'
import React, { useEffect, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { HybridLayoutDefaultFieldPropsType } from '../hybridLayout.types'
import { formatLocalDate } from '../../../utils/date.utils'
import {
  componentNames,
  DATE_RESTRICTION_TYPES,
  EMAIL_REGEX,
  NAMESPACES,
  PHONE_REGEX,
} from '../../../utils/constants'
import { entitiesRoute, viewRoute } from '../../../routes/route.constant'
import { LayoutResponseType } from '@app-store'
import { ElementType } from '../../../types/layout.types'
import { getValidationsFromElement } from '../../../utils/form.utils'

export const HybridFieldViewRenderer = ({
  element,
  fullLayout,
}: {
  element: ElementType
  fullLayout?: LayoutResponseType
}) => {
  const field = useWatch({ name: element?.keyName })
  const singleLookupId = useWatch({
    name: element?.parent ? `${element?.parent?.lookupKeyName}.id` : `__NULL__`,
  })
  const [isHyperLink, setIsHyperlink] = useState<boolean>(false)

  const checkHyperLink = () => {
    if (!field) return false

    const hyperlinkTypes = [
      componentNames.URL,
      componentNames.WEBSITE,
      componentNames.EMAIL,
      componentNames.UNIQUE_ID,
    ] as string[]

    if (element?.isCallable) {
      return true
    }

    // if (isThisAParentCaseIdHyperLink(element) && singleLookupValue?.id) {
    //   return true
    // }

    if (
      element?.elementName === componentNames.UNIQUE_ID &&
      element?.meta?.d_entity_id === fullLayout?.entity_id &&
      !window?.location?.pathname?.includes('/workflow/review/') &&
      (fullLayout?.default_value || element?.meta?.d_entity_id === 32)
    ) {
      return false
    }

    return hyperlinkTypes.includes(element.elementName)
  }

  const onHyperlinkClick = () => {
    if (element?.elementName === componentNames.EMAIL) {
      window.open(`mailto:${field}`, '_blank')
    }
    if (element?.elementName === componentNames.SINGLE_LOOKUP) {
      openNewWindow(
        `/entity/${element?.meta?.d_entity_id}/workflow/review/${field?.id}`
      )
    } else if (element?.elementName === componentNames.UNIQUE_ID) {
      const entityId = element?.meta?.d_entity_id

      if (!isEmpty(element?.parent)) {
        if (!singleLookupId) {
          return
        }
        openNewWindow(
          `/${entitiesRoute}/${entityId}/${viewRoute}/${singleLookupId}`
        )
      } else
        openNewWindow(`/${entitiesRoute}/${entityId}/${viewRoute}/${field?.id}`)
    } else {
      window.open(field, '_blank')
    }
  }

  useEffect(() => {
    setIsHyperlink(checkHyperLink())
  }, [element, field])

  return (
    <div className="font-primary font-light">
      <label
        htmlFor={element?.keyName}
        className="text-sm font-normal mb-1 block"
      >
        {element?.meta?.label}
      </label>
      <div
        onClick={isHyperLink ? onHyperlinkClick : undefined}
        className={`text-xs font-extraLight ${
          isHyperLink ? 'text-secondary-500' : ''
        }`}
      >
        {(element?.elementName === componentNames.DATE_TIME
          ? formatLocalDate(field, 'MM/DD/YYYY', false)
          : field) || '-'}
      </div>
    </div>
  )
}

// DATE TIME
export const HybridLayoutDateTimeField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, mode, fullLayout }) => {
    const { control } = useFormContext()

    const setupDateRange = () => {
      const today = new Date().toISOString().split('T')[0]
      const dateRange: { minDate: string; maxDate: string } = {
        minDate: '',
        maxDate: '',
      }
      if (
        element?.meta?.control_values?.[0] === DATE_RESTRICTION_TYPES?.FUTURE
      ) {
        dateRange.maxDate = today
      } else if (
        element?.meta?.control_values?.[0] === DATE_RESTRICTION_TYPES?.PAST
      ) {
        dateRange.minDate = today
      }
      return dateRange
    }
    const formatDateTimeLocal = (value?: string) => {
      if (!value) return ''
      const date = new Date(value)
      return date.toISOString().slice(0, 16)
    }
    return (
      <div>
        {mode === 'view' ? (
          <HybridFieldViewRenderer element={element} fullLayout={fullLayout} />
        ) : (
          <Controller
            name={element?.keyName || ''}
            control={control}
            render={({ field }) => (
              <FormDateInput
                min={setupDateRange().minDate}
                max={setupDateRange().maxDate}
                {...field}
                value={formatDateTimeLocal(field.value)}
                control={control}
                label={element?.meta?.label}
                isDisabled={
                  !isEmpty(element?.parent)
                    ? true
                    : typeof isDisabled !== 'undefined'
                    ? isDisabled
                    : !!element?.isDisabled
                }
                required={element?.meta?.required ?? false}
                type={DATE_FIELD_TYPES.DATE_TIME_LOCAL}
              />
            )}
          />
        )}
      </div>
    )
  })
HybridLayoutDateTimeField.displayName = 'HybridLayoutDateTimeField'

// CHECKBOX
export const HybridLayoutCheckboxField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled }) => {
    const {
      register,
      formState: { errors },
    } = useFormContext()
    return (
      <div>
        <Checkbox
          labelClassName="break-all"
          label={element?.meta?.label || ''}
          name={element?.keyName || ''}
          register={register}
          disabled={
            !isEmpty(element?.parent)
              ? true
              : typeof isDisabled !== 'undefined'
              ? isDisabled
              : !!element?.isDisabled
          }
          errors={errors}
          required={element?.meta?.required ?? false}
        />
      </div>
    )
  })
HybridLayoutCheckboxField.displayName = 'HybridLayoutCheckboxField'

// RADIO
export const HybridLayoutRadioField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, fullLayout, mode }) => {
    const {
      register,
      formState: { errors },
    } = useFormContext()
    const disabled = !isEmpty(element?.parent)
      ? true
      : typeof isDisabled !== 'undefined'
      ? isDisabled
      : !!element?.isDisabled

    const getOptions = () => {
      const optionsArray = (element?.meta?.control_values || []).map(
        (opt: string) => ({
          label: opt,
          value: opt,
        })
      )
      return optionsArray
    }
    return (
      <div>
        {mode === 'view' ? (
          <HybridFieldViewRenderer element={element} fullLayout={fullLayout} />
        ) : (
          <>
            <div className="font-primary font-light">
              <label htmlFor={element?.keyName} className="text-xl font-medium">
                {element?.meta?.label}
                {element.meta.required ? (
                  <span
                    className={`ml-1 text-xs leading-1 font-extraLight  ${
                      disabled ? 'text-accent-300' : 'text-error-500'
                    }`}
                  >
                    *
                  </span>
                ) : null}
              </label>
            </div>
            <CustomRadioGroup
              tabIndex={element.tabIndex}
              options={getOptions()}
              register={register}
              flexDirection={'flex flex-wrap'}
              name={element.keyName}
              disabled={disabled}
              optionClasses={'text-sm font-normal'}
              labelClasses="w-full block"
              dataQaId={element.keyName ?? ''}
              errors={errors}
              required={element?.meta?.required ?? false}
            />
          </>
        )}
      </div>
    )
  })
HybridLayoutRadioField.displayName = 'HybridLayoutRadioField'

// EMAIL
export const HybridLayoutEmailField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, fullLayout, mode }) => {
    const {
      register,
      formState: { errors },
    } = useFormContext()
    return (
      <div>
        {mode === 'view' ? (
          <HybridFieldViewRenderer element={element} fullLayout={fullLayout} />
        ) : (
          <FormInput
            label={element?.meta?.label}
            register={register}
            validation={{
              ...getValidationsFromElement(element),
              pattern: {
                value: EMAIL_REGEX,
                message: NAMESPACES.EMAIL_VALIDATION_MSG,
              },
            }}
            name={element?.keyName || ''}
            disabled={
              !isEmpty(element?.parent)
                ? true
                : typeof isDisabled !== 'undefined'
                ? isDisabled
                : !!element?.isDisabled
            }
            errors={errors}
            required={element?.meta?.required ?? false}
          />
        )}
      </div>
    )
  })
HybridLayoutEmailField.displayName = 'HybridLayoutEmailField'

//MULTI LINE
export const HybridLayoutMultiLineField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, fullLayout, mode }) => {
    const {
      register,
      formState: { errors },
    } = useFormContext()
    return (
      <div>
        {mode === 'view' ? (
          <HybridFieldViewRenderer element={element} fullLayout={fullLayout} />
        ) : (
          <MultiLineInput
            label={element?.meta?.label}
            register={register}
            name={element?.keyName || ''}
            disabled={
              !isEmpty(element?.parent)
                ? true
                : typeof isDisabled !== 'undefined'
                ? isDisabled
                : !!element?.isDisabled
            }
            errors={errors}
            required={element?.meta?.required ?? false}
          />
        )}
      </div>
    )
  })
HybridLayoutMultiLineField.displayName = 'HybridLayoutMultiLineField'

//PHONE NUMBER
export const HybridLayoutPhoneNumberField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, fullLayout, mode }) => {
    const {
      register,
      formState: { errors },
      setValue,
      getValues,
    } = useFormContext()

    const getPhoneKeys = getValues('__FORM_ORIGINAL_VALUES_PHONE_FIELDS__')
    if (!getPhoneKeys?.includes(element?.meta?.originalKeyName)) {
      setValue('__FORM_ORIGINAL_VALUES_PHONE_FIELDS__', [
        ...(getPhoneKeys || []),
        element?.meta?.originalKeyName,
      ])
    }

    return (
      <div>
        {mode === 'view' ? (
          <HybridFieldViewRenderer element={element} fullLayout={fullLayout} />
        ) : (
          <PhoneInput
            label={element?.meta?.label}
            register={register}
            validation={{
              ...getValidationsFromElement(element),
              pattern: {
                value: PHONE_REGEX,
                message: NAMESPACES.PHONE_VALIDATION_MSG,
              },
            }}
            disabled={
              !isEmpty(element?.parent)
                ? true
                : typeof isDisabled !== 'undefined'
                ? isDisabled
                : !!element?.isDisabled
            }
            setValue={setValue}
            name={element?.keyName || ''}
            errors={errors}
            required={element?.meta?.required ?? false}
          />
        )}
      </div>
    )
  })
HybridLayoutPhoneNumberField.displayName = 'HybridLayoutPhoneNumberField'

//DROPDOWN
export const HybridLayoutDropdownField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, fullLayout, mode }) => {
    const {
      control,
      formState: { errors },
      setValue,
    } = useFormContext()

    return (
      <div>
        {mode === 'view' ? (
          <HybridFieldViewRenderer element={element} fullLayout={fullLayout} />
        ) : (
          <ControlledDropDown
            tabIndex={element.tabIndex}
            name={element.keyName}
            errors={errors}
            label={element.meta.label}
            control={control}
            isSearchable={true}
            onChange={(e: DropdownType<string>) => {
              setValue(element.keyName, e?.value)
            }}
            options={(
              element?.meta?.control_values as unknown as string[]
            )?.map((item: string) => ({
              label: item ?? '',
              value: item ?? '',
            }))}
            required={element.meta.required ?? false}
            disabled={isDisabled}
            dataQaId={element.keyName ?? ''}
            customValidation={(value, options, required) => {
              if (required) {
                const defaultOptionsExists =
                  element?.keyName !== NAMESPACES.RECORD_STATUS_LOWERCASE ||
                  options.some((opt) => opt.value === value)
                return defaultOptionsExists || NAMESPACES.REQUIRED_FIELD
              }
              return true
            }}
          />
        )}
      </div>
    )
  })
HybridLayoutDropdownField.displayName = 'HybridLayoutDropdownField'

// DEFAULT FIELD
export const HybridLayoutDefaultField: React.FC<HybridLayoutDefaultFieldPropsType> =
  React.memo(({ element, isDisabled, mode = 'edit', fullLayout }) => {
    const {
      register,
      formState: { errors },
    } = useFormContext()

    return (
      <div>
        {mode === 'view' ? (
          <HybridFieldViewRenderer element={element} fullLayout={fullLayout} />
        ) : (
          <FormInput
            label={element?.meta?.label}
            register={register}
            name={element?.keyName || ''}
            disabled={
              !isEmpty(element?.parent)
                ? true
                : typeof isDisabled !== 'undefined'
                ? isDisabled
                : !!element?.isDisabled
            }
            errors={errors}
            required={element?.meta?.required ?? false}
          />
        )}
      </div>
    )
  })
HybridLayoutDefaultField.displayName = 'HybridLayoutDefaultField'
